import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { App as AntdApp } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import AppWrapper from '@/components/AppWrapper';
import InternetConnectivityModal from '@/components/InternetConnectivityModal';
import ProtectedRoute from '@/components/ProtectedRoute';
import ProtectedRouteAdmin from '@/components/ProtectedRouteAdmin';
import PublicRoute from '@/components/PublicRoute';
import MainLayout from '@/components/Layout';
import AdminLayout from '@/components/LayoutAdmin';
import LazyPage from '@/components/LazyPage';
import PageLoader from '@/components/PageLoader';

import { setupAuthBroadcastListener } from '@/utils/authSession';

import {
  Login,
  Tracking,
  Settings,
  NotFound,
  ForgotPassword,
  Signup,
  EnquiriesListNew,
  NewEnquiryWrapper,
  AdminDashboard,
  AdminFeedback,
  AdminEventList,
  AdminOrgList,
  AdminEnquiriesList,
  AdminEnquiriesDetails,
  PreAlertPage,
  ReturnPreAlertPage,
  AdminWHList,
  AdminUsers,
  PublicPage,
  AdminProgramManager,
  IndividualEnquiry,
  AdminIndividualEnquiry,
  LabelPage,
  Manifest,
  EnquiryV2,
  EventManifestReportV2,
  EventShowDashboard,
  FMDashboard,
  FMDashboardMetrics,
  FMDashboardOperations,
  FMDashboardShows,
  FMDashboardReturns,
  FMDashboardCarriers,
  FMDashboardAdmin,
} from './pages';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  useEffect(() => {
    const cleanup = setupAuthBroadcastListener();
    return cleanup;
  }, []);




  return (
    <QueryClientProvider client={queryClient}>
      <AntdApp className="!font-montserrat">
        <InternetConnectivityModal />
        <Router>
          <AppWrapper>
            <Routes>

              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LazyPage
                      fallback={<PageLoader message="Loading login..." />}
                      pageName="Login"
                    >
                      <Login />
                    </LazyPage>
                  </PublicRoute>
                }
              />

              <Route path="/forgot-password" element={
                <PublicRoute>
                  <LazyPage
                    fallback={<PageLoader message="Loading home..." />}
                    pageName="ForgotPassword"
                  >
                    <ForgotPassword />
                  </LazyPage>
                </PublicRoute>
              } />

              <Route path="/signup" element={
                <PublicRoute>
                  <LazyPage
                    fallback={<PageLoader message="Loading home..." />}
                    pageName="Signup"
                  >
                    <Signup />
                  </LazyPage>
                </PublicRoute>
              } />

              {/* Public route - no authentication */}
              <Route
                path="/mantainance"
                element={
                  <LazyPage
                    fallback={<PageLoader message="Loading..." />}
                    pageName="PublicPage"
                  >
                    <PublicPage />
                  </LazyPage>
                }
              />



              {/* Protected Routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }>

                <Route index element={
                  <LazyPage
                    fallback={<PageLoader message="Loading home..." />}
                    pageName="Home"
                  >
                    <EnquiriesListNew />
                  </LazyPage>
                } />

                <Route path="enquiries" element={
                  <LazyPage
                    fallback={<PageLoader message="Loading Enquiries..." />}
                    pageName="Enquiries"
                  >
                    <NewEnquiryWrapper />
                  </LazyPage>
                } />

                <Route path="enquiries/individual" element={
                  <LazyPage
                    fallback={<PageLoader message="Loading Individual Enquiry..." />}
                    pageName="IndividualEnquiry"
                  >
                    <IndividualEnquiry />
                  </LazyPage>
                } />

                <Route path="enquiries/:id" element={
                  <LazyPage
                    fallback={<PageLoader message="Loading Enquiry Details..." />}
                    pageName="EnquiriesDetails"
                  >
                    <AdminEnquiriesDetails />
                  </LazyPage>
                } />

                <Route path="pre-alert/:id" element={
                  <LazyPage
                    fallback={<PageLoader message="Loading Pre-Alert..." />}
                    pageName="PreAlertPage"
                  >
                    <PreAlertPage />
                  </LazyPage>
                } />

                <Route path="return-pre-alert/:id" element={
                  <LazyPage
                    fallback={<PageLoader message="Loading Return Pre-Alert..." />}
                    pageName="ReturnPreAlertPage"
                  >
                    <ReturnPreAlertPage />
                  </LazyPage>
                } />

                <Route path="create-label" element={
                  <LazyPage
                    fallback={<PageLoader message="Loading Label..." />}
                    pageName="LabelPage"
                  >
                    <LabelPage />
                  </LazyPage>
                } />


                <Route path="tracking" element={
                  <LazyPage
                    fallback={<PageLoader message="Loading Tracking List..." />}
                    pageName="Tracking"
                  >
                    <Tracking />
                  </LazyPage>
                } />

                <Route path="settings" element={
                  <LazyPage
                    fallback={<PageLoader message="Loading settings..." />}
                    pageName="Settings"
                  >
                    <Settings />
                  </LazyPage>
                } />

                {/* 404 route - must be last */}
                <Route path="*" element={
                  <LazyPage
                    fallback={<PageLoader message="Loading..." />}
                    pageName="NotFound"
                  >
                    <NotFound />
                  </LazyPage>
                } />
              </Route>

              <Route
                path="/admin"
                element={
                  <ProtectedRouteAdmin>
                    <AdminLayout />
                  </ProtectedRouteAdmin>
                }
              >

                <Route index element={<Navigate to="/admin/organisers" replace />} />

                <Route
                  path="/admin/event-manifest-report"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading manifest report..." />}
                      pageName="EventManifestReport"
                    >
                      <EventManifestReportV2 />
                    </LazyPage>
                  }
                />

                <Route
                  path="/admin/dashboard"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading dashboard..." />}
                      pageName="AdminDashboard"
                    >
                      <AdminDashboard />
                    </LazyPage>
                  }
                />

                <Route
                  path="/admin/new-enquiry"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading dashboard..." />}
                      pageName="EnquiryByAdmin"
                    >
                      <AdminIndividualEnquiry />
                    </LazyPage>
                  }
                />

                <Route
                  path="/admin/manual-enquiry"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading manual enquiry..." />}
                      pageName="EnquiryV2"
                    >
                      <EnquiryV2 />
                    </LazyPage>
                  }
                />

                <Route
                  path="/admin/feedbacks"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading home..." />}
                      pageName="AdminFeedback"
                    >
                      <AdminFeedback />
                    </LazyPage>
                  }
                />

                <Route
                  path="/admin/organisers"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading home..." />}
                      pageName="AdminOrgList"
                    >
                      <AdminOrgList />
                    </LazyPage>
                  }
                />


                <Route
                  path="/admin/events"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading home..." />}
                      pageName="AdminEventList"
                    >
                      <AdminEventList />
                    </LazyPage>
                  }
                />

                <Route
                  path="/admin/enquiries"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading home..." />}
                      pageName="AdminEnquiriesList"
                    >
                      <AdminEnquiriesList />
                    </LazyPage>
                  }
                />

                <Route
                  path="/admin/enquiries/:id"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading home..." />}
                      pageName="AdminEnquiriesDetails"
                    >
                      <AdminEnquiriesDetails />
                    </LazyPage>
                  }
                />

                <Route
                  path="/admin/warehouses"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading home..." />}
                      pageName="AdminWarehousesList"
                    >
                      <AdminWHList />
                    </LazyPage>
                  }
                />

                <Route
                  path="/admin/users"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading home..." />}
                      pageName="AdminUsers"
                    >
                      <AdminUsers />
                    </LazyPage>
                  }
                />

                <Route
                  path="/admin/program-management"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading home..." />}
                      pageName="AdminProgramManager"
                    >
                      <AdminProgramManager />
                    </LazyPage>
                  }
                />

                <Route
                  path="/admin/event-show-dashboard"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading event dashboard..." />}
                      pageName="EventShowDashboard"
                    >
                      <EventShowDashboard />
                    </LazyPage>
                  }
                />

                <Route
                  path="/admin/fm-dashboard"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading FM dashboard..." />}
                      pageName="FMDashboard"
                    >
                      <FMDashboard />
                    </LazyPage>
                  }
                >

                  <Route index element={<Navigate to="/admin/fm-dashboard/metrics" replace />} />

                  <Route
                    path="metrics"
                    element={
                      <LazyPage
                        fallback={<PageLoader message="Loading metrics..." />}
                        pageName="FMDashboardMetrics"
                      >
                        <FMDashboardMetrics />
                      </LazyPage>
                    }
                  />

                  <Route
                    path="operations"
                    element={
                      <LazyPage
                        fallback={<PageLoader message="Loading operations..." />}
                        pageName="FMDashboardOperations"
                      >
                        <FMDashboardOperations />
                      </LazyPage>
                    }
                  />

                  <Route
                    path="shows"
                    element={
                      <LazyPage
                        fallback={<PageLoader message="Loading shows..." />}
                        pageName="FMDashboardShows"
                      >
                        <FMDashboardShows />
                      </LazyPage>
                    }
                  />
                  <Route
                    path="returns"
                    element={
                      <LazyPage
                        fallback={<PageLoader message="Loading returns..." />}
                        pageName="FMDashboardReturns"
                      >
                        <FMDashboardReturns />
                      </LazyPage>
                    }
                  />

                  <Route
                    path="carriers"
                    element={
                      <LazyPage
                        fallback={<PageLoader message="Loading carriers..." />}
                        pageName="FMDashboardCarriers"
                      >
                        <FMDashboardCarriers />
                      </LazyPage>
                    }
                  />

                  <Route
                    path="admin"
                    element={
                      <LazyPage
                        fallback={<PageLoader message="Loading admin..." />}
                        pageName="FMDashboardAdmin"
                      >
                        <FMDashboardAdmin />
                      </LazyPage>
                    }
                  />

                </Route>

                <Route
                  path="/admin/manifest"
                  element={
                    <LazyPage
                      fallback={<PageLoader message="Loading manifest..." />}
                      pageName="Manifest"
                    >
                      <Manifest />
                    </LazyPage>
                  }
                />

              </Route>


            </Routes>
          </AppWrapper>
        </Router>
      </AntdApp>

      {/* React Query DevTools - only in development */}
      {/* {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />} */}
    </QueryClientProvider>
  );
}

export default App;
